import {
  IComponentPayload,
  IPracticeComponent,
  LibraryChild,
  LibraryDetail,
  LibraryItem,
  LibraryItemType,
  LibraryMeta,
  PracticeComponentType
} from 'interfaces/practice-component'

// Card-meta icon names (SvgIcon), matching the BuilderLibrary card design:
// duration + notes on the first row, source plan + child count on the second.
const ICON_CLOCK = 'ic_outline-alarm'
const ICON_COUNT = 'ic_outline-account-tree'
const ICON_NOTES = 'note-rallypd'
const ICON_PLAN = 'training-plan'

// Direct-child noun per component level, used for "N lanes" / "N drills" summaries.
const CHILD_NOUN: Record<PracticeComponentType, string> = {
  period: 'lane',
  lane: 'block',
  block: 'station',
  station: 'drill',
  rotation: 'lane'
}

function pluralize(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`
}

function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1)
}

/** Direct-child count of a payload node (drills for a station, else children). */
function childCountOf(node: IComponentPayload): number {
  return node.type === 'station' ? node.drills?.length ?? 0 : node.children?.length ?? 0
}

function summarizeNode(node: IComponentPayload): string {
  return pluralize(childCountOf(node), CHILD_NOUN[node.type])
}

/**
 * Build the right-panel preview detail from a component's payload.
 * Only available on the detail DTO (list results exclude payload).
 */
function buildDetailFromPayload(payload: IComponentPayload): LibraryDetail {
  const root = payload.root as { note?: string; duration?: number }
  const detail: LibraryDetail = {
    childLabel: capitalize(CHILD_NOUN[payload.type]) + 's',
    notes: root.note
  }

  if (payload.type === 'station') {
    // The prescription is part of what a coach saved the station for, so the
    // preview shows it the way the inspector does.
    detail.trainingRx = payload.trainingRx
    detail.children = (payload.drills ?? []).map<LibraryChild>(drill => ({
      name: drill.name ?? 'Drill',
      meta: typeof drill.duration === 'number' ? `${drill.duration} min` : ''
    }))
  } else {
    detail.children = (payload.children ?? []).map<LibraryChild>(child => ({
      name: (child.root?.name as string) ?? '',
      meta: summarizeNode(child)
    }))
  }

  return detail
}

/**
 * Map a PracticeComponent DTO to a BuilderLibrary card view model.
 * Meta chips read denormalized fields (no payload needed); `detail` is filled
 * only when the detail payload is present (GET /practice-components/{id}).
 */
export function mapComponentToLibraryItem(dto: IPracticeComponent): LibraryItem {
  const type = dto.type as LibraryItemType
  // Order matters — the card lays these out in a Wrap, so it reads
  // "duration · notes / plan · N children". Absent fields drop their row
  // entirely rather than rendering a placeholder.
  // Tags are NOT repeated here; they already render as chips above the meta rows.
  const meta: LibraryMeta[] = []
  if (dto.durationLabel) meta.push({ icon: ICON_CLOCK, text: dto.durationLabel })
  if (dto.notes) meta.push({ icon: ICON_NOTES, text: dto.notes })
  if (dto.planName) meta.push({ icon: ICON_PLAN, text: dto.planName })
  // metaSummary is the BE-pluralized "N lanes" string — using it keeps the
  // singular/plural rule in one place instead of re-deriving it from childType.
  if (dto.metaSummary) meta.push({ icon: ICON_COUNT, text: dto.metaSummary })

  const item: LibraryItem = {
    id: dto.id,
    key: `${dto.type}:${dto.id}`,
    title: dto.name,
    type,
    meta,
    tags: dto.tags
  }

  if (dto.payload) {
    item.detail = buildDetailFromPayload(dto.payload)
    if (dto.durationLabel) {
      item.detail.duration = dto.durationLabel
    }
  }

  return item
}
